import { createSlice } from "@reduxjs/toolkit";
import {
    BUY_URL,
    MERCHANTS_URL,
    REDEEM_URL,
    REDEEM_TIMEOUT_SECONDS,
    RESEND_NOTIFICATION_URL,
    CANCEL_URL
} from "./constants";
import { MerchantIdentifier } from "../enum/merchants";
import { AppThunk } from "./store";
import { canTranslateErrorCode, translateErrorCode } from "./services/errorCodeTranslator";
import { SliceStatusResult, SliceStatusResultExtentions } from "./models/sliceStatusResult";
import { merchantIdentifierToName } from "./hooks/merchantIdentifier";

export interface AvailableMerchants {
    name: string;
    vouchers: { guid: string; amount: number; count: number }[];
    minimumExpirationDays?: number;
    id: number;
    classAppend: string;
    logo: string;
    appIcon: string;
    sendsVoucherCodes: boolean;
}

export interface VoucherState {
    status: {
        isLoading: boolean;
        result: SliceStatusResult;
    };
    merchants: AvailableMerchants[];
    chosenMerchant: AvailableMerchants;
    chosenGuid: { guid: string; amount: number };
    chosenPaymentId: string;
    phoneNumber: string;
    purchaseCompleted: { completed: boolean; result: string };
}

const initialState: VoucherState = {
    status: { isLoading: false, result: { type: "", code: "", description: "" } },
    merchants: [],
    chosenMerchant: {
        name: "",
        vouchers: [{ guid: "", amount: 0, count: 0 }],
        id: 0,
        classAppend: "",
        logo: "",
        appIcon: "",
        sendsVoucherCodes: false
    },
    chosenGuid: { guid: "", amount: 0 },
    chosenPaymentId: "",
    phoneNumber: "",
    purchaseCompleted: { completed: false, result: "" }
};

export const voucherSlice = createSlice({
    name: "voucher",
    initialState,
    reducers: {
        start: state => {
            state.status.isLoading = true;
            state.status.result = { type: "", code: "", description: "" };
        },

        stop(state, action) {
            state.status.isLoading = false;
            state.status.result = action.payload;
        },

        availableMerchants(state, action) {
            state.merchants = action.payload;
        },

        merchantChoice(state, action) {
            state.chosenMerchant = action.payload;
        },

        amountChoice(state, action) {
            state.chosenGuid = action.payload;
        },

        voucherReservation(state, action) {
            state.chosenPaymentId = action.payload;
        },

        setPhoneNumber(state, action) {
            state.phoneNumber = action.payload;
        },

        clearResultStatus: state => {
            state.status.result = { type: "", code: "", description: "" };

        },

        completePurchase(state, action) {
            state.purchaseCompleted = action.payload;
        }
    }
});

export const fetchMerchants = (): AppThunk => {
    return async dispatch => {
        dispatch(start());
        try {
            const response = await fetch(MERCHANTS_URL);

            if (!response.ok) {
                throw new Error();
            }
            const data = await response.json();

            const toKebabCase = function (str: string) {
                return str
                    .replace(/([A-Z])/g, "-$1")
                    .slice(1)
                    .toLowerCase();
            };

            let merchantsList: AvailableMerchants[] = data.map(
                (merchant: {
                    merchantIdentifier: number;
                    minimumExpirationDays?: number;
                    vouchers: { guid: string; amount: number; count: number; }[];
                    sendsVoucherCodes: boolean;
                }) => {
                    let availableMerchant = {
                        name: merchantIdentifierToName(merchant.merchantIdentifier),
                        vouchers: merchant.vouchers,
                        id: merchant.merchantIdentifier,
                        minimumExpirationDays: merchant.minimumExpirationDays,
                        classAppend: toKebabCase(
                            MerchantIdentifier[merchant.merchantIdentifier]
                        ),
                        logo: `/assets/logos/${toKebabCase(
                            MerchantIdentifier[merchant.merchantIdentifier]
                        )}-logo.svg`,
                        appIcon: `/assets/app-icons/${toKebabCase(
                            MerchantIdentifier[merchant.merchantIdentifier]
                        )}-app.png`,
                        sendsVoucherCodes: merchant.sendsVoucherCodes
                    };
                    return availableMerchant;
                }
            );

            dispatch(availableMerchants(merchantsList));
            dispatch(stop(SliceStatusResultExtentions.succeeded()));
            return;
        } catch (err) {
            dispatch(
                stop(translateErrorCode('LOCAL02'))
            );
            console.log(err);
        }
    };
};

let redeemPoll: NodeJS.Timeout;

export const sendVoucherRequest = (requestBody: {
    voucherGuid: string;
    phoneNumber: string;
}): AppThunk => {
    return async dispatch => {
        dispatch(start());
        try {
            const response = await fetch(BUY_URL, {
                headers: { "Content-Type": "application/json", accept: "text/plain" },
                body: JSON.stringify(requestBody),
                method: "POST"
            });

            if (!response.ok) {
                const data = await response.json();
                if (canTranslateErrorCode(data.code)) {
                    dispatch(stop(translateErrorCode(data.code)));
                }
                else {
                    dispatch(stop(SliceStatusResultExtentions.failedPayment()));
                }
                return;
            }

            const data = await response.json();

            dispatch(voucherReservation(data.paymentId));

            let attempts: number = 0;
            const sleepIntervalSeconds = 5;
            const maxAttempts: number = REDEEM_TIMEOUT_SECONDS / sleepIntervalSeconds;

            const redeemRequest = async () => {
                const response = await fetch(REDEEM_URL, {
                    headers: { "Content-Type": "application/json", accept: "text/plain" },
                    body: JSON.stringify(data),
                    method: "POST"
                });

                attempts++;
                if (response.ok) {
                    dispatch(completePurchase({ completed: true, result: "success" }));
                    dispatch(stop(SliceStatusResultExtentions.succeeded()));
                    clearInterval(redeemPoll);
                    return;
                }

                if (attempts >= maxAttempts) {
                    dispatch(completePurchase({ completed: true, result: "failed" }));
                    dispatch(stop(SliceStatusResultExtentions.failedPayment()));
                    clearInterval(redeemPoll);
                    return;
                } else {
                    const data = await response.json();

                    if (data.code === "PCS103") {
                        return;
                    } else if (canTranslateErrorCode(data.code)) {
                        dispatch(completePurchase({ completed: true, result: "failed" }));
                        localStorage.removeItem('paymentId');
                        dispatch(stop(translateErrorCode(data.code)));
                        clearInterval(redeemPoll);
                    } else {
                        dispatch(completePurchase({ completed: true, result: "failed" }));
                        dispatch(stop(SliceStatusResultExtentions.failedPayment()));
                        clearInterval(redeemPoll);
                        return;
                    }
                }
            };
            redeemPoll = setInterval(redeemRequest, sleepIntervalSeconds * 1000);
        } catch (err) {
            console.log(err);
            dispatch(completePurchase({ completed: true, result: "failed" }))
            dispatch(stop(translateErrorCode(err)));
        }
    };
};

export const cancelPurchase = (requestBody: { paymentId: string }): AppThunk => {
    return async dispatch => {
        clearInterval(redeemPoll);
        dispatch(stop({ type: "", code: "", description: "" }));
        try {
            const response = await fetch(CANCEL_URL, {
                headers: { "Content-Type": "application/json", accept: "text/plain" },
                body: JSON.stringify(requestBody),
                method: "POST"
            });
            if (!response.ok) {
                const data = await response.json();
                dispatch(stop({ type: "error", code: data.code, description: data.description }));
            }
            dispatch(completePurchase({ completed: false, result: "" }))
            dispatch(voucherReservation(""));
            dispatch(merchantChoice({
                name: "",
                vouchers: [{ guid: "", amount: 0, count: 0 }],
                id: 0,
                classAppend: "",
                logo: "",
                appIcon: ""
            }));
            dispatch(amountChoice({ guid: "", amount: 0 }));

        } catch (err) {
            dispatch(stop({ type: "error", code: err, description: err }));
        }
    }
}

export const resendPaymentNotification = (paymentId: string): AppThunk => {
    return async dispatch => {
        await fetch(RESEND_NOTIFICATION_URL, {
            headers: { "Content-Type": "application/json", accept: "text/plain" },
            body: JSON.stringify({ paymentId: paymentId }),
            method: "POST"
        });
    }
};

export const {
    start,
    stop,
    availableMerchants,
    amountChoice,
    voucherReservation,
    merchantChoice,
    setPhoneNumber,
    clearResultStatus,
    completePurchase
} = voucherSlice.actions;

export default voucherSlice.reducer;
